import { PredictionAdmin, TaskAdmin } from '@gleamer/types';
import classNames from 'classnames';
import React, { FormEvent, useRef, useState } from 'react';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PredictionImportDetail } from './PredictionImportDetail';
import {
  inFlight,
  useImportPredictionFile,
  usePredictionFileImports,
  usePredictionFiles,
} from '../../../../hooks/usePredictionFileImports';
import { usePredictionProducts } from '../../../../hooks/usePredictionProducts';
import { PERMISSIONS } from '../../../../utils/permissions';
import { PageButton } from '../../../atoms/PageButton';
import { PageTitle } from '../../../atoms/PageTitle';
import { Card, Field, FilePicker, inputClassName, selectClassName } from '../../../atoms/Surface';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatBytes, formatDateTime } from '../imports/format';
import { ProgressBar } from '../imports/ProgressBar';
import { ImportStatusBadge } from '../tasks/ActivityParts';

type PredictionImport = TaskAdmin.PredictionImport;
type PredictionProduct = PredictionAdmin.PredictionProduct;

export const PAGE_SIZE = 50;
/** What a prediction file is: newline-delimited JSON. */
export const ACCEPTED_FILES = '.json,.ndjson,.jsonl';

/** The title viewer-api gives an import when none is: the file name without its extension. */
export function defaultTitle(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '');
}

/** The file's name, from the object it was uploaded to (`api/<uuid>/<name>`). */
export function sourceName(imported: PredictionImport): string {
  return imported.sourceObject?.split('/').pop() ?? '—';
}

function errorText(error: unknown): string {
  return (error as Error)?.message ?? String(error);
}

function NewImport({ products }: { products: PredictionProduct[] }) {
  const importFile = useImportPredictionFile();
  const fileInput = useRef<HTMLInputElement>(null);
  const [productId, setProductId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!file || !productId) {
      return;
    }
    importFile.mutate(
      {
        productId: Number(productId),
        file,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
      },
      {
        onSuccess: () => {
          setFile(null);
          setTitle('');
          setDescription('');
          if (fileInput.current) {
            fileInput.current.value = '';
          }
        },
      }
    );
  };
  return (
    <Card
      title="Import a file"
      description="Newline-delimited JSON, one prediction a line. It is uploaded, then read in the background: the page shows how far it has got, and an import interrupted by a restart carries on where it stopped."
    >
      {products.length === 0 ? (
        <p className="text-dl-muted">Create a product first: every import belongs to one.</p>
      ) : (
        <form
          aria-label="Import a file"
          onSubmit={submit}
          className="flex flex-wrap items-start gap-4"
        >
          <Field label="Product">
            <select
              className={classNames(selectClassName, 'w-56')}
              value={productId}
              onChange={e => setProductId(e.target.value)}
              required
            >
              <option value="">Choose a product</option>
              {products.map(p => (
                <option
                  key={p.id}
                  value={p.id}
                >
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="File">
            <FilePicker
              aria-label="Prediction file"
              inputRef={fileInput}
              file={file}
              accept={ACCEPTED_FILES}
              onChange={e => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          <Field
            label="Title"
            hint="The file name when left empty."
          >
            <input
              className={classNames(inputClassName, 'w-56')}
              value={title}
              placeholder={file ? defaultTitle(file.name) : undefined}
              onChange={e => setTitle(e.target.value)}
              maxLength={200}
            />
          </Field>
          <Field
            label="Description"
            className="min-w-[16rem] flex-1"
          >
            <input
              className={inputClassName}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Field>
          <div className="flex items-center gap-2 pt-6">
            <PageButton
              tone="primary"
              type="submit"
              disabled={!file || !productId || importFile.isLoading}
            >
              {importFile.isLoading ? 'Uploading…' : 'Import'}
            </PageButton>
            {importFile.isError && (
              <span
                role="alert"
                className="text-sm text-dl-danger"
              >
                {errorText(importFile.error)}
              </span>
            )}
            {importFile.isSuccess && !importFile.isLoading && (
              <span
                role="status"
                className="text-sm text-dl-success"
              >
                Import {importFile.data.id} requested.
              </span>
            )}
          </div>
        </form>
      )}
    </Card>
  );
}

const FILE_STATUS: Record<
  PredictionAdmin.PredictionFile['status'],
  { label: string; className: string }
> = {
  LOADING: { label: 'Reading', className: 'bg-dl-tint text-dl-accent' },
  LOADED: { label: 'Loaded', className: 'bg-dl-success-tint text-dl-success' },
  ACCEPTED: { label: 'Accepted', className: 'bg-dl-success-tint text-dl-success' },
  FAILED: { label: 'Refused', className: 'bg-dl-danger-tint text-dl-danger' },
  SKIPPED: { label: 'Skipped', className: 'bg-dl-track text-dl-muted' },
};

/**
 * The files dropped in the predictions bucket: a file in a folder goes to the folder's product, one at the root to
 * each algorithm name its lines carry; one naming no product is refused, and shown here with why.
 */
function DroppedFiles() {
  const [offset, setOffset] = useState(0);
  const files = usePredictionFiles({ limit: PAGE_SIZE, offset });
  return (
    <Card
      title="Dropped in the bucket"
      description="Files the AI team drops in the predictions bucket: in a folder, imported into the folder's product; at the root, into each algorithm name their lines carry. A file with a line naming no product is refused."
    >
      {files.isLoading && <p className="text-dl-muted">Loading the files…</p>}
      {files.isError && (
        <p
          role="alert"
          className="text-dl-danger"
        >
          Could not load the files: {errorText(files.error)}
        </p>
      )}
      {files.isSuccess && files.data.length === 0 && offset === 0 && (
        <p className="text-dl-muted">No file dropped yet.</p>
      )}
      {files.isSuccess && (files.data.length > 0 || offset > 0) && (
        <>
          <div className="-mx-6 overflow-x-auto">
            <table
              className="w-full table-auto"
              aria-label="Dropped files"
            >
              <thead>
                <tr>
                  <TableHeadTh className="pl-6">File</TableHeadTh>
                  <TableHeadTh>Status</TableHeadTh>
                  <TableHeadTh>Imports</TableHeadTh>
                  <TableHeadTh className="pr-6">Received</TableHeadTh>
                </tr>
              </thead>
              <tbody>
                {files.data.map(file => (
                  <tr
                    key={file.id}
                    className="align-top"
                    aria-label={`File ${file.objectName}`}
                  >
                    <TableBodyTd className="max-w-md break-all pl-6 font-mono text-sm">
                      {file.objectName}
                    </TableBodyTd>
                    <TableBodyTd>
                      <span
                        className={classNames(
                          'rounded px-2 py-0.5 text-sm font-medium',
                          FILE_STATUS[file.status].className
                        )}
                      >
                        {FILE_STATUS[file.status].label}
                      </span>
                      {file.error && (
                        <p className="mt-1 max-w-md break-words text-sm text-dl-danger">
                          {file.error}
                        </p>
                      )}
                    </TableBodyTd>
                    <TableBodyTd className="text-sm">
                      {file.importIds.length === 0
                        ? '—'
                        : file.importIds.map(id => `#${id}`).join(', ')}
                    </TableBodyTd>
                    <TableBodyTd className="whitespace-nowrap pr-6 text-dl-muted">
                      {formatDateTime(file.receivedAt)}
                    </TableBodyTd>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <PageButton
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
            >
              Previous
            </PageButton>
            <PageButton
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={files.data.length < PAGE_SIZE}
            >
              Next
            </PageButton>
          </div>
        </>
      )}
    </Card>
  );
}

/** How far a file import has read its file: bytes read against the file's size. */
function FileProgress({ imported }: { imported: PredictionImport }) {
  const size = imported.sourceSize ?? 0;
  const read = Math.min(imported.bytesRead ?? 0, size);
  if (!inFlight(imported)) {
    return <span className="text-dl-muted">{formatBytes(imported.sourceSize)}</span>;
  }
  return (
    <div className="flex min-w-[10rem] flex-col gap-1">
      <ProgressBar
        label={`Import ${imported.id} progress`}
        segments={[
          { label: 'Read', value: read, className: 'bg-dl-accent' },
          { label: 'To read', value: size - read, className: 'bg-dl-track' },
        ]}
      />
      <span className="text-xs text-dl-muted">
        {formatBytes(read)} of {formatBytes(size)}
        {size > 0 && ` · ${Math.floor((100 * read) / size)} %`}
      </span>
    </div>
  );
}

function ImportRow({
  imported,
  product,
  open,
  onOpen,
}: {
  imported: PredictionImport;
  product?: PredictionProduct;
  open: boolean;
  onOpen: () => void;
}) {
  return (
    <tr
      className="align-top"
      aria-label={`Import ${imported.id}`}
    >
      <TableBodyTd className="pl-6">
        <button
          type="button"
          aria-expanded={open}
          onClick={onOpen}
          className="text-left font-medium text-dl-accent hover:underline"
        >
          {imported.title ?? sourceName(imported)}
        </button>
        {imported.description && (
          <p className="max-w-md break-words text-sm text-dl-muted">{imported.description}</p>
        )}
      </TableBodyTd>
      <TableBodyTd>{product?.name ?? `#${imported.productId}`}</TableBodyTd>
      <TableBodyTd
        className="max-w-[16rem] break-all font-mono text-sm text-dl-muted"
        title={imported.sourceObject ?? undefined}
      >
        {sourceName(imported)}
        {imported.sourceAlgorithm && (
          <p className="font-sans text-xs">the lines of {imported.sourceAlgorithm}</p>
        )}
        {imported.predictionFileId != null && (
          <p className="font-sans text-xs">dropped in the bucket</p>
        )}
      </TableBodyTd>
      <TableBodyTd>
        <ImportStatusBadge status={imported.status} />
        {imported.accepted && imported.status !== 'DONE' && (
          <p className="mt-1 text-xs text-dl-muted">accepted, settling</p>
        )}
        {imported.exportTable && (
          <p
            className="mt-1 max-w-xs break-all font-mono text-xs text-dl-muted"
            title="Its BigQuery table"
          >
            {imported.exportTable}
          </p>
        )}
        {imported.failure && (
          <p className="mt-1 max-w-xs break-words text-sm text-dl-danger">{imported.failure}</p>
        )}
      </TableBodyTd>
      <TableBodyTd>
        <FileProgress imported={imported} />
      </TableBodyTd>
      <TableBodyTd className="text-right tabular-nums">
        {imported.linesRead.toLocaleString()}
      </TableBodyTd>
      <TableBodyTd className="text-right tabular-nums">
        {imported.predictionsImported.toLocaleString()}
      </TableBodyTd>
      <TableBodyTd className="text-right tabular-nums">
        {imported.predictionsDropped.toLocaleString()}
      </TableBodyTd>
      <TableBodyTd className="text-right tabular-nums">
        {(imported.predictionsReplaced ?? 0).toLocaleString()}
      </TableBodyTd>
      <TableBodyTd className="whitespace-nowrap pr-6 text-dl-muted">
        {formatDateTime(imported.requestedAt)}
      </TableBodyTd>
    </tr>
  );
}

function ImportList({
  products,
  canDecide,
  canPreview,
}: {
  products: PredictionProduct[];
  canDecide: boolean;
  canPreview: boolean;
}) {
  const [productId, setProductId] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const imports = usePredictionFileImports({
    productId: productId ? Number(productId) : undefined,
    limit: PAGE_SIZE,
    offset,
  });
  const byId = new Map(products.map(p => [p.id, p]));
  const opened = imports.data?.find(i => i.id === openId);
  return (
    <Card
      title="Imports"
      actions={
        <Field label="Product">
          <select
            aria-label="Show the imports of"
            className={classNames(selectClassName, 'w-56')}
            value={productId}
            onChange={e => {
              setProductId(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Every product</option>
            {products.map(p => (
              <option
                key={p.id}
                value={p.id}
              >
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      }
    >
      {imports.isLoading && <p className="text-dl-muted">Loading the imports…</p>}
      {imports.isError && (
        <p
          role="alert"
          className="text-dl-danger"
        >
          Could not load the imports: {errorText(imports.error)}
        </p>
      )}
      {imports.isSuccess && imports.data.length === 0 && offset === 0 && (
        <p className="text-dl-muted">No file imported yet.</p>
      )}
      {imports.isSuccess && (imports.data.length > 0 || offset > 0) && (
        <>
          <div className="-mx-6 overflow-x-auto">
            <table
              className="w-full table-auto"
              aria-label="Prediction imports"
            >
              <thead>
                <tr>
                  <TableHeadTh className="pl-6">Title</TableHeadTh>
                  <TableHeadTh>Product</TableHeadTh>
                  <TableHeadTh>Source</TableHeadTh>
                  <TableHeadTh>Status</TableHeadTh>
                  <TableHeadTh>Progress</TableHeadTh>
                  <TableHeadTh className="text-right">Lines read</TableHeadTh>
                  <TableHeadTh className="text-right">Imported</TableHeadTh>
                  <TableHeadTh className="text-right">Dropped</TableHeadTh>
                  <TableHeadTh className="text-right">Replaced</TableHeadTh>
                  <TableHeadTh className="pr-6">Requested</TableHeadTh>
                </tr>
              </thead>
              <tbody>
                {imports.data.map(imported => (
                  <ImportRow
                    key={imported.id}
                    imported={imported}
                    product={byId.get(imported.productId ?? -1)}
                    open={openId === imported.id}
                    onOpen={() => setOpenId(openId === imported.id ? null : imported.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <PageButton
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
            >
              Previous
            </PageButton>
            <PageButton
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={imports.data.length < PAGE_SIZE}
            >
              Next
            </PageButton>
          </div>
          {opened && (
            <div className="mt-6">
              <PredictionImportDetail
                imported={opened}
                products={products}
                canDecide={canDecide}
                canPreview={canPreview}
                onClose={() => setOpenId(null)}
              />
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * The backoffice's Prediction imports page (read:prediction): the files imported into products, newest first,
 * with how far each has got, and the files dropped in the bucket with the imports each started or why none. A
 * data manager (import:prediction) uploads a file and imports it into a product, titled and described; an import
 * opened shows what it holds and lets a data manager (create:prediction) settle, move or delete it.
 */
export function PredictionImportsPage() {
  const { has } = usePermissions();
  const products = usePredictionProducts();
  const all = products.data ?? [];
  return (
    <div className="flex flex-col gap-6">
      <PageTitle title="Prediction imports" />
      {products.isError && (
        <p
          role="alert"
          className="text-dl-danger"
        >
          Could not load the products: {errorText(products.error)}
        </p>
      )}
      {has(PERMISSIONS.IMPORT_PREDICTION) && products.isSuccess && <NewImport products={all} />}
      <ImportList
        products={all}
        canDecide={has(PERMISSIONS.CREATE_PREDICTION)}
        canPreview={has(PERMISSIONS.READ_PREVIEW)}
      />
      <DroppedFiles />
    </div>
  );
}

export default PredictionImportsPage;
